import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import DashboardBuySell from "./DashboardBuySell";
import { useAuth } from "../context/AuthContext";
import { useTrading } from "../context/TradingContext";
import {
  useAvailableAssets,
  useFeatureFlagsByCategory,
} from "../hooks/query/queries";
import { usePairStore } from "../store/useStore";
import BuySellCarousel from "./BuySellCarousel";
import { useSendFlowState } from "../hooks/useSendFlowState";
import { Skeleton } from "./ui/skeleton";

export default function TradingInterface({ selectedAsset: propSelectedAsset }) {
  const { data: featureFlags } = useFeatureFlagsByCategory("mainFeatures");

  // Don't render if both buyCrypto and sellCrypto are disabled
  const isBuySellEnabled = featureFlags?.buyCrypto || featureFlags?.sellCrypto;

  const location = useLocation();
  const {
    tabActive,
    selectedCrypto,
    type,
    selectedTab,
    parentTab,
    childTab,
    parentNavTab,
  } = location.state || {};

  const { setPairObject } = usePairStore();
  const {
    tradingAssets,
    pairObject,
    walletBalance: walletBalanceData,
  } = useTrading();
  const { clearState } = useSendFlowState();

  useEffect(() => {
    if (pairObject) setPairObject(pairObject);
  }, [pairObject, setPairObject]);

  const [activeTab, setActiveTab] = useState("overview");
  const [transactionTab, setTransactionTab] = useState("");
  const [asset, setAsset] = useState(null);
  const [firstTimeUser, setFirstTimeUser] = useState(true);
  const wallet = useMemo(() => {
    if (!walletBalanceData) return null;
    return {
      ...walletBalanceData,
      assetBalances: {
        crypto: (walletBalanceData?.assets || []).filter(
          (asset) => asset.currencyType === "Crypto" || asset.type === "Crypto"
        ),
        fiat: (walletBalanceData?.assets || []).filter(
          (asset) => asset.currencyType === "Fiat" || asset.type === "Fiat"
        ),
      },
    };
  }, [walletBalanceData]);

  const normalizedAsset = useMemo(() => {
    if (!propSelectedAsset || !tradingAssets) return null;

    const assetIdentifier =
      propSelectedAsset.asset?.toUpperCase() ||
      propSelectedAsset.symbol?.toUpperCase() ||
      propSelectedAsset.info?.asset?.toUpperCase();

    if (!assetIdentifier) return null;

    const matchedAsset = tradingAssets.find(
      (ta) =>
        ta.asset?.toUpperCase() === assetIdentifier ||
        ta.assetName?.toUpperCase() === assetIdentifier
    );

    if (matchedAsset) {
      return matchedAsset;
    }
    return {
      ...propSelectedAsset,
      assetId:
        propSelectedAsset.assetId ||
        propSelectedAsset._id ||
        propSelectedAsset.info?._id,
      asset:
        propSelectedAsset.asset ||
        propSelectedAsset.symbol?.toLowerCase() ||
        propSelectedAsset.info?.asset,
      assetName:
        propSelectedAsset.assetName ||
        propSelectedAsset.info?.assetName ||
        propSelectedAsset.symbol,
      assetIcon:
        propSelectedAsset.assetIcon ||
        propSelectedAsset.image ||
        propSelectedAsset.info?.image,
    };
  }, [propSelectedAsset, tradingAssets]);

  const { user: userProfile } = useAuth();

  const userId = userProfile?._id;

  const zarBalance = useMemo(() => {
    if (wallet?.assets) {
      const zarAsset = wallet.assets.find(
        (asset) => asset.asset === "zar" || asset.asset === "ZAR"
      );
      return zarAsset?.availableAmount ?? 0;
    }
    return wallet?.assetBalances?.fiat?.zar?.availableAmount ?? 0;
  }, [wallet]);

  const params = {
    id: userId,
  };

  const { data: assetsOwned, isLoading: isLoadingAssetsOwned } =
    useAvailableAssets({
      isSend: true,
      isEightyeightPay: false,
      investArray: params,
      balanceParams: { ...params },
      balanceParamsEightEight: params,
    });

  useEffect(() => {
    if (
      Array.isArray(assetsOwned) &&
      assetsOwned.length > 0 &&
      assetsOwned.some((asset) => asset.availableAmount > 0)
    ) {
      setFirstTimeUser(false);
    }
  }, [assetsOwned]);

  useEffect(() => {
    clearState();
  }, [activeTab, clearState]);

  useEffect(() => {
    if (parentTab) {
      setActiveTab(parentTab);
    }
    if (type && tabActive) {
      setTransactionTab(tabActive);
      setActiveTab(type);
      setAsset(selectedCrypto || normalizedAsset);
    }
    if (parentNavTab) {
      setActiveTab(parentNavTab);
    }
  }, [
    parentTab,
    type,
    parentNavTab,
    tabActive,
    selectedCrypto,
    normalizedAsset,
  ]);

  useEffect(() => {
    if (normalizedAsset && !type && !tabActive) {
      setAsset(normalizedAsset);
      if (!transactionTab) {
        setTransactionTab("Buy");
      }
    } else if (normalizedAsset && !selectedCrypto) {
      setAsset(normalizedAsset);
    } else if (selectedCrypto) {
      setAsset(selectedCrypto);
    }
  }, [normalizedAsset, type, tabActive, transactionTab, selectedCrypto]);

  // Hide component if buy/sell features are disabled
  if (!isBuySellEnabled) {
    return null;
  }

  return isLoadingAssetsOwned ? (
    <>
      <div className="flex flex-col w-full p-8 shadow-lg bg-white rounded-2xl">
        <div className="mb-8">
          <div className="grid grid-cols-2 rounded-full h-full bg-white border border-neutral p-1 gap-2">
            <Skeleton className="h-12 rounded-full bg-gray-300" />
            <Skeleton className="h-12 rounded-full bg-gray-300" />
          </div>
        </div>

        <div className="space-y-6 mb-6">
          <div className="space-y-2">
            <Skeleton className="h-4 w-24 bg-gray-300" />

            <div className="flex items-center justify-between border border-gray-200 rounded-lg p-4">
              <div className="flex items-center gap-3">
                <Skeleton className="h-10 w-10 rounded-full bg-gray-300" />
                <Skeleton className="h-6 w-20 rounded-lg bg-gray-300" />
              </div>
              <div className="flex items-center gap-3">
                <Skeleton className="h-10 w-10 rounded-full bg-gray-300" />
              </div>
            </div>

            <div className="flex items-center gap-6">
              <Skeleton className="h-4 w-24 bg-gray-300 mt-4" />
              <Skeleton className="h-4 w-24 bg-gray-300 mt-4 ml-16" />
            </div>

            <div className="flex items-center gap-4">
              <div className="flex items-center border border-gray-200 rounded-lg p-4 w-1/2">
                <div className="flex items-center gap-3">
                  <Skeleton className="h-10 w-10 rounded-full bg-gray-300" />
                  <Skeleton className="h-6 w-20 rounded-lg bg-gray-300" />
                </div>
              </div>
              <div className="flex items-center border border-gray-200 rounded-lg p-4 w-full">
                <div className="flex items-center gap-3">
                  <Skeleton className="h-8 w-20 rounded-lg bg-gray-300" />
                </div>
              </div>
            </div>

            <div className="flex items-center gap-4">
              <Skeleton className="h-4 w-24 bg-gray-300 mt-4" />
            </div>

            <div className="flex items-center justify-between border border-gray-200 rounded-lg p-4">
              <div className="flex items-center gap-3">
                <Skeleton className="h-8 w-20 rounded-lg bg-gray-300" />
              </div>
              <div className="flex items-center gap-3">
                <Skeleton className="h-6 w-16 rounded-full bg-gray-300" />
                <Skeleton className="h-10 w-10 rounded-full bg-gray-300" />
                <Skeleton className="h-10 w-20 rounded-full bg-gray-300" />
              </div>
            </div>
          </div>
        </div>

        <div className="flex justify-center pt-4">
          <Skeleton className="h-12 w-[300px] rounded-lg bg-gray-300" />
        </div>
      </div>
    </>
  ) : firstTimeUser ? (
    <BuySellCarousel
      onGetStarted={() => setFirstTimeUser(false)}
      fromDashboard={true}
    />
  ) : (
    <DashboardBuySell
      assetToSelect={asset}
      pairObject={pairObject}
      tab={transactionTab}
      quickActionTab={selectedTab}
      childTab={childTab}
      tradingAssets={tradingAssets}
      zarBalance={zarBalance}
      onDashboard={true}
    />
  );
}
